// [S-092] Attestation records — admin browser.
//
// Lists every certified template visible to the caller (platform_admin sees
// L1; tenant_admin sees own L2; tenant_user is 403 from the route guard).
// Per-row: template id, layer chip, lifecycle state, anchor CBA, attorney,
// attestation date, truncated hash. The table pattern follows the
// re-attestation schedule view so the visual language stays consistent —
// the list-view design is a deliberate choice in S-092 (the wireframe set
// only ships a detail view; see Attestation.html).
//
// Filtering is client-side debounced: typing into the search input
// re-fetches via the `useAttestationsList` query whose key includes the
// filter object. Empty inputs are omitted from the request so the server
// returns the full visible set.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  LoadingState,
  PageHead,
  Pagination,
  usePagination,
} from "../components";
import {
  useAttestationsList,
  type AttestationRow,
  type AttestationsFilter,
} from "../data/attestationsQuery";
import { useActorLabels } from "../data/identitiesQuery";
import { useSession } from "../auth/AuthProvider";

// We don't carry a human title from the server (the entity only has
// `content`) — render a readable label from deal type + guild instead of
// the opaque templateId.
function titleFor(row: AttestationRow): string {
  const dealTypePretty = row.dealType.replace(/_/g, " ");
  return `${capitalize(row.guild)} ${capitalize(dealTypePretty)}`;
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

export function Attestations(): JSX.Element {
  const session = useSession();
  const tenantId =
    session?.studioId !== null && session?.studioId !== undefined
      ? `tenant-${session.studioId}`
      : "";
  const [attorney, setAttorney] = useState("");
  const [cba, setCba] = useState("");
  const filter: AttestationsFilter = useMemo(() => {
    const f: AttestationsFilter = {};
    if (attorney.trim().length > 0) f.attorney = attorney.trim();
    if (cba.trim().length > 0) f.cba = cba.trim();
    return f;
  }, [attorney, cba]);

  const { data, isLoading, isError, refetch, isFetching } =
    useAttestationsList(filter);
  // Called unconditionally (before the loading/error early returns) per the
  // rules of hooks — degrades to an empty uid list pre-load.
  const ownerLabels = useActorLabels(
    data?.attestations.map((r) => r.attestedBy) ?? [],
    tenantId
  );
  const pager = usePagination(data?.attestations ?? [], 25);
  const showLoading = isLoading || data === undefined;

  return (
    <>
      <PageHead
        eyebrow="Approval records"
        title={
          <>
            The chain of <em>custody</em>.
          </>
        }
        sub={
          showLoading
            ? "Fetching the approval trail."
            : isError
              ? "The admin API responded with an error. Try again in a moment."
              : "Every certified template carries a verifiable approval trail — who reviewed it, what CBA it's anchored to, and a tamper-evident fingerprint of the approved document."
        }
        cta={
          !showLoading && !isError ? (
            <>
              <span className="reatt__head__refresh">
                {data.attestations.length}{" "}
                {data.attestations.length === 1 ? "record" : "records"}
              </span>
              <Button
                variant="secondary"
                className="btn--s"
                onClick={() => {
                  void refetch();
                }}
                disabled={isFetching}
                data-testid="att-refresh"
              >
                {isFetching ? "Refreshing…" : "Refresh"}
              </Button>
            </>
          ) : undefined
        }
      />

      {showLoading && <LoadingState label="Loading attestations…" />}

      {!showLoading && isError && (
        <Alert label="Records unavailable" tone="error">
          The admin API responded with an error. Try again in a moment.
        </Alert>
      )}

      {!showLoading && !isError && (
        <>
          <div className="att-list__filters" data-testid="att-filters">
            <div className="att-list__filter">
              <label htmlFor="att-attorney">Attorney</label>
              <input
                id="att-attorney"
                type="search"
                placeholder="search by attorney…"
                value={attorney}
                onChange={(e) => setAttorney(e.target.value)}
                data-testid="att-filter-attorney"
              />
            </div>
            <div className="att-list__filter">
              <label htmlFor="att-cba">Anchor CBA</label>
              <input
                id="att-cba"
                type="search"
                placeholder="exact match, e.g. SAG-AFTRA 2023"
                value={cba}
                onChange={(e) => setCba(e.target.value)}
                data-testid="att-filter-cba"
              />
            </div>
          </div>

          {!isFetching && data.attestations.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="tbl tbl--audit tbl--att" data-testid="att-table">
              <div className="tbl__row tbl__row--head">
                <span>Template</span>
                <span>Layer</span>
                <span>State</span>
                <span>Anchor CBA</span>
                <span>Attorney</span>
                <span>Attested</span>
                <span>Pre-image hash</span>
              </div>
              {pager.pageItems.map((row) => (
                <AttestationRowView
                  key={row.templateId}
                  row={row}
                  ownerLabel={ownerLabels?.get(row.attestedBy)}
                />
              ))}
            </div>
          )}

          {data.attestations.length > 0 && (
            <Pagination
              page={pager.page}
              pageCount={pager.pageCount}
              onPageChange={pager.setPage}
              total={pager.total}
              rangeStart={pager.rangeStart}
              rangeEnd={pager.rangeEnd}
              itemLabel="record"
            />
          )}
        </>
      )}
    </>
  );
}

function AttestationRowView({
  row,
  ownerLabel,
}: {
  row: AttestationRow;
  ownerLabel: string | undefined;
}): JSX.Element {
  return (
    <div className="tbl__row" data-testid={`att-row-${row.templateId}`}>
      <div className="att-list__tmpl">
        <span className="src-edge src-edge--cert" aria-hidden="true" />
        <Link to={`/attestation/${encodeURIComponent(row.templateId)}`}>
          {titleFor(row)}
        </Link>
      </div>
      <span>
        <span
          className={`att-list__layer att-list__layer--${row.layerSource.toLowerCase()}`}
        >
          {row.layerSource}
        </span>
      </span>
      <span>
        <span
          className={
            row.lifecycleState === "re_certified"
              ? "att-list__state att-list__state--rec"
              : "att-list__state att-list__state--cert"
          }
        >
          {row.lifecycleState}
        </span>
      </span>
      <span className="att-list__cba">{row.anchorCBA ?? "—"}</span>
      <span className="att-list__who">{ownerLabel ?? "—"}</span>
      <span className="att-list__when">{formatShortDate(row.attestedAt)}</span>
      <span className="att-list__hash" title={row.preImageHash}>
        {row.preImageHash.slice(0, 12)}…{row.preImageHash.slice(-4)}
      </span>
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="att-list__empty" data-testid="att-empty">
      <b>No attestation records found</b>
      No certified or re-certified templates match the current filter. Clear
      the filters to see all visible attestations.
    </div>
  );
}

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
